import { positionChecker } from 'src/logic'
import { I18NText, Position, State, StateObj } from 'src/types'
import { getBaseCell, getColorCell } from 'src/util/simulationsSpecification'

// 基本項目ロジック
export const getGorillaBaseCells = (state: State, position: Position) => {
  const { isPitcher, isMitt } = positionChecker(position)
  const productNumber = state.baseModel.productNumber
  const baseModelStateObj = {
    label: productNumber,
    value: productNumber
  }
  const isBomberSeries = isPitcher && state.series?.value === 'bomber'
  return [
    getBaseCell('基本モデル', baseModelStateObj),
    isPitcher && getBaseCell('シリーズ', state?.series),
    getBaseCell('利き腕', state.dominantArm),
    isBomberSeries && getBaseCell('サイズ', state.size),
    !isBomberSeries && getBaseCell('指カバー/指当て', state.fingerGuardType),
    !isBomberSeries && !isMitt && getBaseCell('ウェブパーツ', state.webParts),
    !isMitt && getBaseCell('背面紐通し', state.backLace)
  ].filter(Boolean)
}

export const checkGorillaWeb2 = (webPartsValue: string): boolean => {
  return ['w26'].includes(webPartsValue)
}

// グラブ / カラー項目
export const getGorillaGloveColorCells = (state: State, t: I18NText) => {
  const needFingerCoverOrPad = state.fingerGuardType?.value !== 'none'
  const needWeb2 = checkGorillaWeb2(state?.webParts?.value)
  const productNumber = state.baseModel.productNumber
  const is001Model = ['001-20'].includes(productNumber) // 投手
  const isBknModel = ['BKN-20'].includes(productNumber) // 外野手
  return [
    getColorCell(t.CATCH_SURFACE, state.catchFace, 'catchFace'),
    getColorCell(t.WEB, state.web, 'web'),
    needWeb2 && getColorCell(t.WEB2, state.web2, 'web2'),
    getColorCell(t.THUMB_HOOK, state.thumbHook, 'thumbHook'),
    getColorCell(t.LITTLE_HOOK, state.littleHook, 'littleHook'),
    needFingerCoverOrPad && getColorCell(t.FINGER_GUARD, state.indexCover, 'indexCover'),
    getColorCell(t.LININGS, state.linings, 'linings'),
    getColorCell(t.THUMB1, state.thumbOut, 'thumbOut'),
    getColorCell(t.THUMB2, state.thumbWeb, 'thumbWeb'),
    getColorCell(t.INDEX + '1', state.indexWeb, 'indexWeb'),
    getColorCell(t.INDEX + '2', state.indexMiddle, 'indexMiddle'),
    getColorCell(t.MIDDLE + '1', state.middleIndex, 'middleIndex'),
    getColorCell(t.MIDDLE + '2', state.middleRing, 'middleRing'),
    getColorCell(t.RING + '1', state.ringMiddle, 'ringMiddle'),
    getColorCell(t.RING + '2', state.ringLittle, 'ringLittle'),
    getColorCell(t.LITTLE + '1', state.littleRing, 'littleRing'),
    getColorCell(t.LITTLE + '2', state.littleOut, 'littleOut'),
    !is001Model && getColorCell(t.BAND, state.listBelt, 'listBelt'),
    isBknModel && getColorCell('BKN1', state?.littleOut, 'littleOut'),
    isBknModel && getColorCell('BKN2', state?.littleRing, 'littleRing'),
    getColorCell(t.LACE, state.lace, 'lace'),
    getColorCell(t.EDGE, state.edge, 'edge'),
    getColorCell(t.STITCH, state.stitch, 'stitch'),
    getColorCell(t.HAMIDASHI, state.hamidashi, 'hamidashi'),
    getColorCell('ムートン', state.mouton, 'mouton'),
    getColorCell('ごりら印ラベル', state?.gorillaLabel, 'gorillaLabel')
  ].filter(Boolean)
}

// ファーストミット / カラー項目
export const getGorillaFirstMittColorCells = (state: State, t: I18NText) => {
  const needFingerCoverOrPad = state.fingerGuardType.value !== 'none'
  return [
    getColorCell(t.CATCH_SURFACE, state.catchFace, 'catchFace'),
    getColorCell(t.WEB, state.web, 'web'),
    getColorCell(t.THUMB_HOOK, state.thumbHook, 'thumbHook'),
    getColorCell(t.LITTLE_HOOK, state.littleHook, 'littleHook'),
    needFingerCoverOrPad && getColorCell(t.FINGER_GUARD, state.indexCover, 'indexCover'),
    getColorCell(t.LININGS, state.linings, 'linings'),
    getColorCell(t.THUMB, state.thumb, 'thumb'),
    getColorCell(t.UNDER_WEB, state.underWeb, 'underWeb'),
    getColorCell(t.BOOMERANG, state.boomerang, 'boomerang'),
    getColorCell(t.BAND, state.listBelt, 'listBelt'),
    getColorCell(t.LACE, state.lace, 'lace'),
    getColorCell(t.EDGE, state.edge, 'edge'),
    getColorCell(t.STITCH, state.stitch, 'stitch'),
    getColorCell('ムートン', state.mouton, 'mouton'),
    getColorCell('ごりら印ラベル', state?.gorillaLabel, 'gorillaLabel')
  ]
}

// キャッチャーミット / カラー項目
export const getGorillaCatcherMittColorCells = (state: State, t: I18NText) => {
  const needFingerCoverOrPad = state.fingerGuardType.value !== 'none'
  return [
    getColorCell(t.CATCH_SURFACE, state.catchFace, 'catchFace'),
    getColorCell(t.WEB, state.web, 'web'),
    getColorCell(t.THUMB_HOOK, state.thumbHook, 'thumbHook'),
    getColorCell(t.LITTLE_HOOK, state.littleHook, 'littleHook'),
    needFingerCoverOrPad && getColorCell(t.FINGER_GUARD, state.indexCover, 'indexCover'),
    getColorCell(t.LININGS, state.linings, 'linings'),
    getColorCell(t.STAND, state.fingerStand, 'fingerStand'),
    getColorCell('親指・人差指・中指1', state.thumbIndexMiddleRight, 'thumbIndexMiddleRight'),
    getColorCell('中指2・薬指・小指', state.middleLeftRingLittle, 'middleLeftRingLittle'),
    getColorCell(t.EDGE, state.edge, 'edge'),
    getColorCell(t.LACE, state.lace, 'lace'),
    getColorCell(t.STITCH, state.stitch, 'stitch'),
    getColorCell(t.HAMIDASHI, state.hamidashi, 'hamidashi'),
    getColorCell('ムートン', state.mouton, 'mouton'),
    getColorCell('ごりら印ラベル', state?.gorillaLabel, 'gorillaLabel')
  ].filter(Boolean)
}
